import { Box } from "opentok-layout-js";
import { IMessage } from "react-native-gifted-chat";

export interface Tenant {
  tenantId: number;
  title: string;
  domain: string;
  logo: string;
  icon: string;
  websocketDomain: string;
  color: string;
  settings: {
    useMemberDirectory: boolean;
    useChat: boolean;
    useChannels: boolean;
    useConnections: boolean;
    menu: {
      showGroups: boolean;
      showBlogs: boolean;
      showEvents: boolean;
      showFeed: boolean;
    };
  };
}

export interface WolUser {
  profileId: number;
  firstName: string;
  lastName: string;
  avatarUrl: string;
  coverPhoto: string;
  company: string;
  title: string;
  accessCode: string;
  description: string;
  website: string;
  memberSince: string;
  location: string;
  settings: {
    useConnections: boolean;
  };
  isTenantAdmin: boolean;
  private: boolean;
}

export interface Group {
  groupId: number;
  groupName: string;
  groupDescription: string;
  avatarUrl: string;
  groupCoverPhoto: string;
  admin: string;
  groupAccessCode: string;
}

export interface Venue {
  eventId: number;
  name: string;
  description: string;
  summary: string;
  accessCode: string;
  createDate: string;
  eventStartTime: string;
  eventEndTime: string;
  registrationStartTime: string;
  registrationEndTime: string;
  coverPhoto: string;
  group: Group;
  categories: Array<string>;
  tags: Array<string>;
  sponsors: Sponsor[];
  registrants: Attendee[];
  chatTopic: string;
}

export type VenueSessionStatus = "NotStarted" | "InProgress" | "Finished";

export interface VenueSession {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  tags: Array<string>;
  isPaid: boolean;
  isKeynote: boolean;
  presenters: Array<Presenter>;
  sessionId: number;
  chatTopic: string;
  hlsUrl: string | null;
  status: VenueSessionStatus;
  venueId: number;
}

export interface Booth {
  title: string;
  description: string;
  openTimes: Array<{ startTime: Date; endTime: Date }>;
  coverPhotoUrl: string;
  presenters: Array<Presenter>;
  boothId: number;
  chatTopic: string;
}

export interface ApiResponse<T> {
  response: {
    code: number;
    success: boolean;
  };
  results: T;
}

export interface JoinSessionData {
  apiKey: string;
  sessionId: number;
  token: string;
  isRecorded: boolean;
  role: "subscriber" | "moderator" | "publisher";
}

export interface ChatMessage {
  id: number;
  message: string;
  author: number;
  createDate: string;
  topic: string;
  isUnread: boolean;
  imageUrl: string | null;
  isQuestion: boolean;
  isAnswered: boolean;
}

export interface PostChatMessage {
  message: string;
  isQuestion: boolean;
  topic: string;
}

export interface ApiConversation {
  topic: string;
  participants: WolUser[];
  messages: ChatMessage[];
  title: string | null;
  description: string | null;
  isPrivate: boolean;
}

export interface FormattedChatMessage extends IMessage {
  isQuestion: boolean;
  isAnswered: boolean;
  createdAt: number;
}

export interface FormattedConversation extends ApiConversation {
  formattedMessages: Record<string, FormattedChatMessage>;
  formattedParticipants: Record<string, WolUser>;
}

export interface Registration {
  isRegistered: boolean;
}

export interface Presenter {
  name: string;
  title: string;
  bio: string;
  profileId: number;
  avatarUrl: string;
}

export interface BoothDescriptionProps {
  title: string;
  description: string;
  presenters?: Presenter[];
}
export interface PresenterInfoCardProps {
  avatarUrl: string;
  firstName: string;
  lastName: string;
  title: string;
  company: string;
  name: string;
  isSubscriberAudioOn: boolean;
}

export interface StreamObj {
  id: string;
  userInfo: string;
  hasAudio: boolean;
  hasVideo: boolean;
  videoType: string;
  connectionId: string;
}

export interface Attendee {
  profileId?: number;
  bio?: string;
  avatarUrl?: string;
  company: string;
  id?: number;
  name: string;
  title: string;
}

export interface WolAuthInfo {
  access_token: string;
  expires_in: number;
  refresh_token: string;
}

export interface GrantPresenterStatus {
  userId: number;
  requestStatus:
    | "initial"
    | "consentRequested"
    | "consentAccepted"
    | "tokenRequested"
    | "tokenSuccess"
    | "granted"
    | "denied"
    | "error";
}

export interface Sponsor {
  name: string;
  description: string;
  level: number;
  url: string;
  imageUrl: string;
}

export interface HomeParams {
  venueId: string;
}

export interface LoadingParams {
  venueId: string;
  boothId?: string;
  sessionId?: string;
}

export interface VenueConnectionData {
  id: number;
  name: string;
  avatarUrl: string;
  company: string;
  title: string;
  role: JoinSessionData["role"];
}

export type StreamId = string;

export interface VenueSessionLayout {
  screenShare: StreamId | null;
  panel: StreamId[];
}

export const validPublishingStates: VenueSessionState[] = [
  "prePublish",
  "publishing",
  "publisherSetup",
];

export type VenueSessionStyle = "grid" | "full-screen";

export type VenueSessionState =
  | "initial"
  | "publisherSetup"
  | "prePublish"
  | "publishing"
  | "waitingToSubscribe"
  | "subscribing"
  | "moderating";

export interface WOLAlert {
  title: string;
  msg?: string;
  acceptText?: string;
  uuid: string;
}

export type AlertRequest = Omit<WOLAlert, "uuid">;

export interface WOLConfirmation {
  title: string;
  msg?: string;
  uuid: string;
  userAccepted?: boolean;
  acceptText?: string;
  denyText?: string;
}

export type ConfirmationRequest = Omit<
  WOLConfirmation,
  "userAccepted" | "uuid"
>;

export type VenueArchiveLayout = Record<string, Box>;
